import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { ACTUAL_CLINIC_TYPES } from "constants/clinics";
import { REGIONS_SELECTOR_FEATURE } from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser,
  setCurrentClinic,
  setCurrentRegion,
  setUserClinics
} from "features/auth/auth-slice";
import {
  CLINICS_MINIMAL_QUERY,
  useGetClinicsQuery,
  useLazyGetClinicQuery,
  useLazyGetClinicsQuery
} from "services/clinics";
import { useGetRegionsListQuery } from "services/regions";
import { ClinicTypes } from "services/types/clinics.type";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import ClinicSelector from "./clinic-selector.component";
import RegionsSelectorComponent from "./regions-selector/regions-selector.component";

type CurrentClinicSelectorProps = {
  isLargeScreen: boolean;
  minimalView?: boolean;
};

const REGIONS_QUERY =
  "{id,clinics{id,name,timezone,noah_provider,noah_alias,noah_tenant_id},created_at,name,is_active,noah_location_id}";

const CurrentClinicRegionSelector: React.FC<CurrentClinicSelectorProps> = ({
  isLargeScreen,
  minimalView = false
}) => {
  const flags = useFlags();
  const isRegionsSelectorEnabled = flags[REGIONS_SELECTOR_FEATURE];

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const isShowOwnClinicsOnlyEnabled = currentUser.show_own_clinics_only;
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegion = useAppSelector(selectCurrentRegion);

  const localStorageClinic = setupLocalStorageAPI("selectedClinic");
  const { default_clinic: defaultClinic } = currentUser;
  const storedClinic =
    localStorageClinic.getData(defaultClinic) || defaultClinic;
  const currentPracticeId = currentUser?.practice?.id;

  // ---------- memoized args to avoid identity churn ----------
  const regionsArgs = useMemo(
    () => ({
      user: currentUser.id,
      is_active: true,
      query: REGIONS_QUERY
    }),
    [currentUser.id]
  );

  const clinicsArgs = useMemo(() => {
    const args: {
      query: string;
      type: typeof ACTUAL_CLINIC_TYPES;
      user?: number;
    } = {
      query: CLINICS_MINIMAL_QUERY,
      type: ACTUAL_CLINIC_TYPES
    };

    // Only pass user parameter when regions are enabled OR showOwnClinicsOnly is enabled
    if (
      (isRegionsSelectorEnabled || isShowOwnClinicsOnlyEnabled) &&
      currentUser?.id
    ) {
      args.user = currentUser.id;
    }

    return args;
  }, [currentUser?.id, isRegionsSelectorEnabled, isShowOwnClinicsOnlyEnabled]);

  // (Lazy) service locations: only query on demand
  const serviceLocArgs = useMemo(() => {
    const args: {
      query: string;
      type: ClinicTypes[];
      user?: number;
    } = {
      query: CLINICS_MINIMAL_QUERY,
      type: [ClinicTypes.SERVICE_LOCATION]
    };

    // Only pass user parameter when regions are enabled OR showOwnClinicsOnly is enabled
    if (
      (isRegionsSelectorEnabled || isShowOwnClinicsOnlyEnabled) &&
      currentUser?.id
    ) {
      args.user = currentUser.id;
    }

    return args;
  }, [currentUser?.id, isRegionsSelectorEnabled, isShowOwnClinicsOnlyEnabled]);

  // ---------- queries ----------
  const {
    data: regions = [],
    isFetching: regionsIsFetching,
    isSuccess: isRegionsSuccess
  } = useGetRegionsListQuery(regionsArgs, {
    skip: !isRegionsSelectorEnabled || !currentUser.id || currentUser.id === -1
  });

  const { data: clinics = [], isSuccess: isClinicsSuccess } =
    useGetClinicsQuery(clinicsArgs, {
      skip: !currentUser?.id || currentUser.id === -1
    });

  const [triggerServiceLoc, { data: serviceLocationClinics = [] }] =
    useLazyGetClinicsQuery();

  const [getClinicDetails] = useLazyGetClinicQuery();

  // ---------- derived ----------
  const userRegions = useMemo(() => {
    if (!isClinicsSuccess || !isRegionsSuccess || !isRegionsSelectorEnabled)
      return [];
    return regions.map((region) => ({
      ...region,
      clinics: clinics.filter((c) => c.region?.id === region.id)
    }));
  }, [
    regions,
    clinics,
    isClinicsSuccess,
    isRegionsSuccess,
    isRegionsSelectorEnabled
  ]);

  // We'll compute & cache this once during bootstrap to avoid an always-on query.
  const [hasServiceLocations, setHasServiceLocations] =
    useState<boolean>(false);

  // Run the initial "pick current clinic" only once
  const initDoneRef = useRef(false);

  useEffect(() => {
    if (
      initDoneRef.current ||
      !isClinicsSuccess ||
      (isRegionsSelectorEnabled && regionsIsFetching) ||
      currentClinic ||
      !currentPracticeId ||
      clinics.length === 0
    ) {
      return;
    }

    // If regions feature is enabled, we may need service-locations flag exactly once.
    const maybeFetchServiceLocs = async () => {
      if (isRegionsSelectorEnabled) {
        const res = await triggerServiceLoc(serviceLocArgs);
        const list = ("data" in res && res.data) || [];
        setHasServiceLocations(Boolean(list.length > 0));
      } else {
        setHasServiceLocations(false);
      }
    };
    // choose the clinic based on storage + practice
    const updatedStoredClinic = clinics.find((c) => c.id === storedClinic?.id);
    const clinic =
      updatedStoredClinic?.practice?.id === currentPracticeId
        ? updatedStoredClinic
        : clinics[0];
    // we can fire the lazy check in parallel; it's not critical to selecting the clinic
    void maybeFetchServiceLocs();

    // First, immediately set the clinic with minimal details
    dispatch(
      setCurrentClinic({
        currentClinic: clinic,
        numberOfClinics: clinics.length,
        hasServiceLocations: false, // set a provisional value; we'll update after service locations check
        hasRegions: isRegionsSelectorEnabled && Boolean(regions.length > 0)
      })
    );

    // Then fetch and update with full clinic details
    if (clinic?.id) {
      const selectedClinicId = clinic.id;
      getClinicDetails({ clinicId: selectedClinicId })
        .then((result) => {
          if (result.data) {
            // Only update if this is still the currently selected clinic
            dispatch((dispatch, getState) => {
              const currentClinicId = getState().auth.currentClinic?.id;
              if (currentClinicId === selectedClinicId) {
                dispatch(
                  setCurrentClinic({
                    currentClinic: result.data,
                    numberOfClinics: clinics.length,
                    hasServiceLocations: false,
                    hasRegions:
                      isRegionsSelectorEnabled && Boolean(regions.length > 0)
                  })
                );
              }
            });
          }
        })
        .catch((error) => {
          // If fetching full details fails, we still have the minimal details set
          console.error("Failed to fetch full clinic details:", error);
        });
    }

    initDoneRef.current = true;
  }, [
    regionsIsFetching,
    isClinicsSuccess,
    currentClinic,
    currentPracticeId,
    clinics,
    storedClinic,
    dispatch,
    regions.length,
    isRegionsSelectorEnabled,
    triggerServiceLoc,
    serviceLocArgs,
    getClinicDetails
  ]);

  // When currentClinic moves across regions, keep currentRegion in sync (idempotent)
  useEffect(() => {
    if (!currentClinic?.region || !isRegionsSelectorEnabled) return;

    const needUpdate = currentRegion?.id !== currentClinic.region.id;
    if (!needUpdate) return;

    const updatedRegion = userRegions.find(
      (r) => r.id === currentClinic.region?.id
    );
    if (updatedRegion) {
      dispatch(setCurrentRegion(updatedRegion));
    } else {
      dispatch(setCurrentRegion(null));
    }
  }, [
    dispatch,
    currentClinic,
    currentRegion,
    userRegions,
    isRegionsSelectorEnabled
  ]);

  // After lazy service-locations resolves the first time, we can update the store once.
  const appliedSvcLocRef = useRef(false);
  useEffect(() => {
    if (appliedSvcLocRef.current || !initDoneRef.current) return;

    // Only update if we have resolved the service locations question
    if (
      (currentClinic && serviceLocationClinics !== undefined) ||
      hasServiceLocations
    ) {
      dispatch(
        setCurrentClinic({
          currentClinic: currentClinic,
          numberOfClinics: clinics.length,
          hasServiceLocations,
          hasRegions: isRegionsSelectorEnabled && Boolean(regions.length > 0)
        })
      );
      appliedSvcLocRef.current = true;
    }
  }, [
    serviceLocationClinics,
    hasServiceLocations,
    dispatch,
    currentClinic,
    clinics.length,
    isRegionsSelectorEnabled,
    regions.length
  ]);

  // Set user clinics when show_own_clinics_only is enabled for the user
  useEffect(() => {
    if (isShowOwnClinicsOnlyEnabled && isClinicsSuccess && clinics.length > 0) {
      dispatch(setUserClinics(clinics));
    }
  }, [isShowOwnClinicsOnlyEnabled, isClinicsSuccess, clinics, dispatch]);

  if (regionsIsFetching || isRegionsSelectorEnabled === undefined) {
    return null;
  }

  return isRegionsSelectorEnabled && regions.length ? (
    <RegionsSelectorComponent
      minimalView={minimalView}
      regions={regions}
      clinics={clinics}
    />
  ) : (
    <ClinicSelector
      clinics={clinics}
      isLargeScreen={isLargeScreen}
      minimalView={minimalView}
    />
  );
};

export default CurrentClinicRegionSelector;
